import { TextboxProps as CarbonTextBoxProps } from 'carbon-react/lib/components/textbox';
import React, { useEffect, useRef, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import { default as SharedTextBox } from './index';

interface DebouncedTextboxPropsInner extends Omit<CarbonTextBoxProps, 'defaultValue'> {
  value: string;
  onValueChangeDebounced?: (value: string) => void;
  debounceDelay?: number;
}

function DebouncedTextboxInner({ onValueChangeDebounced, onChange, value, debounceDelay = 500, ...props }: DebouncedTextboxPropsInner) {
  const lastValue = useRef(value);
  const debounced = useDebounceCallback((v: string) => {
    onValueChangeDebounced?.(v);
    lastValue.current = v;
  }, debounceDelay);

  // If parent forces value change outside of our emissions, update ref to avoid stale comparison
  useEffect(() => {
    if (value !== lastValue.current) {
      lastValue.current = value;
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    debounced(next);
    onChange?.(e);
  };

  useEffect(() => () => debounced.cancel(), [debounced]);

  return <SharedTextBox {...props} value={value} onChange={handleChange} />;
}

interface DebouncedTextboxProps extends Omit<CarbonTextBoxProps, 'defaultValue' | 'onChange'> {
  value: string;
  onValueChangeDebounced?: (value: string) => void;
  debounceDelay?: number;
  onChange?: (ev: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * A debounced text input for filtering table columns.
 * Syncs with URL query params and handles browser navigation properly.
 */
const DebouncedTextbox = ({ value, onChange, debounceDelay = 500, onValueChangeDebounced, ...props }: DebouncedTextboxProps) => {
  // Local state so transient user input survives potential remounts of child textbox
  const [localValue, setLocalValue] = useState(value ?? '');
  // Track last value we emitted to URL to avoid echo writes
  const lastEmitted = useRef<string | undefined>(value);

  // When external URL-derived value changes (and is not just our own emission), adopt it
  useEffect(() => {
    if (value !== undefined && value !== lastEmitted.current) {
      setLocalValue(value);
    }
  }, [value]);

  const handleImmediateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
    onChange?.(e);
  };

  const handleDebounced = (next: string) => {
    // Skip if value matches external to prevent redundant URL updates
    if (next === lastEmitted.current) return;
    lastEmitted.current = next;
    onValueChangeDebounced?.(next);
  };

  return (
    <DebouncedTextboxInner
      {...props}
      value={localValue}
      debounceDelay={debounceDelay}
      onChange={handleImmediateChange}
      onValueChangeDebounced={handleDebounced}
    />
  );
};

export default DebouncedTextbox;
